import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Eye, 
  Download, 
  Mail, 
  Phone,
  Calendar,
  MapPin,
  Briefcase,
  User,
  FileText,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import AdminLayout from '../../components/admin/AdminLayout'

const ApplicationsManagement = () => {
  const { applications, updateApplication } = useStore()
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const handleStatusChange = (id, newStatus) => {
    updateApplication(id, { status: newStatus })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800'
      case 'reviewed':
        return 'bg-yellow-100 text-yellow-800'
      case 'accepted':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return Clock
      case 'reviewed':
        return Eye
      case 'accepted':
        return CheckCircle
      case 'rejected':
        return XCircle
      default:
        return FileText
    }
  }

  const ApplicationModal = () => {
    if (!selectedApplication) return null

    const StatusIcon = getStatusIcon(selectedApplication.status)

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      >
        <GlassCard className="p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-black">
              Candidature - {selectedApplication.fullName}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Informations personnelles */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-4">Informations personnelles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-primary-500" />
                  <div>
                    <p className="text-sm text-gray-600">Nom complet</p>
                    <p className="font-medium text-black">{selectedApplication.fullName}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-primary-500" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-black">{selectedApplication.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-primary-500" />
                  <div>
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <p className="font-medium text-black">{selectedApplication.phone}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-primary-500" />
                  <div>
                    <p className="text-sm text-gray-600">Date de candidature</p>
                    <p className="font-medium text-black">
                      {new Date(selectedApplication.applicationDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Poste visé */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-4">Poste visé</h3>
              <div className="flex items-center space-x-3">
                <Briefcase className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm text-gray-600">Position</p>
                  <p className="font-medium text-black">{selectedApplication.position}</p>
                </div>
              </div>
            </div>

            {/* Message de motivation */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-4">Message de motivation</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedApplication.coverLetter}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Fermer
              </Button>
              <Button
                variant="secondary"
                icon={Download}
              >
                Télécharger CV
              </Button>
            </div>
          </div>
        </GlassCard>
      </motion.div>
    )
  }

  return (
    <AdminLayout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div>
              <h1 className="text-3xl font-display font-bold gradient-text">
                Gestion des Candidatures
              </h1>
              <p className="text-gray-600 mt-1">
                Gérez les candidatures reçues pour les offres d'emploi
              </p>
            </div>
          </motion.div>

          {/* Applications List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {applications.length === 0 ? (
              <div className="col-span-full">
                <GlassCard className="p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    Aucune candidature
                  </h3>
                  <p className="text-gray-500">
                    Les candidatures apparaîtront ici une fois que les offres d'emploi recevront des réponses.
                  </p>
                </GlassCard>
              </div>
            ) : (
              applications.map((application, index) => {
                const StatusIcon = getStatusIcon(application.status)
                return (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <GlassCard className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-black">
                          {application.fullName}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {application.status === 'new' ? 'Nouvelle' :
                           application.status === 'reviewed' ? 'Examinée' :
                           application.status === 'accepted' ? 'Acceptée' :
                           application.status === 'rejected' ? 'Rejetée' : 'Inconnue'}
                        </span>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Briefcase className="w-4 h-4" />
                          <span>{application.position}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span>{application.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{application.phone}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(application.applicationDate).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {application.coverLetter}
                      </p>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={Eye}
                          onClick={() => {
                            setSelectedApplication(application)
                            setShowModal(true)
                          }}
                          className="flex-1"
                        >
                          Voir détails
                        </Button>
                        
                        {application.status === 'new' && (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={CheckCircle}
                              onClick={() => handleStatusChange(application.id, 'accepted')}
                              className="text-green-600 hover:text-green-700"
                            >
                              Accepter
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={XCircle}
                              onClick={() => handleStatusChange(application.id, 'rejected')}
                              className="text-red-600 hover:text-red-700"
                            >
                              Rejeter
                            </Button>
                          </>
                        )}
                      </div>
                    </GlassCard>
                  </motion.div>
                )
              })
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && <ApplicationModal />}
      </div>
    </AdminLayout>
  )
}

export default ApplicationsManagement
